import { readTranslatedNovelPage } from '$lib/apis/read/api'

type CacheKey = string

export type NovelTranslateQueueStatus = {
  /** Waiting for a concurrency slot. */
  phase: 'queued' | 'active'
  /** 1-based position while queued; 0 when active. */
  position: number
}

type StatusListener = (status: NovelTranslateQueueStatus) => void

type Job = {
  key: CacheKey
  cacheId: string
  page: number
  force: boolean
  controller: AbortController
  promise: Promise<string | null>
  listeners: Set<StatusListener>
  /** True once a concurrency slot was taken (must release on finish/cancel). */
  acquired: boolean
  /** Resolve callback while waiting for a slot. */
  grantSlot?: () => void
}

const textCache = new Map<CacheKey, string>()
/** In-flight jobs — survive virtual-list remounts; components only subscribe. */
const jobs = new Map<CacheKey, Job>()

/** Max concurrent novel translation API calls across the reader (backend-aware). */
let maxConcurrent = 1
let activeCount = 0
/** Jobs waiting for a slot, in enqueue order. */
const waitQueue: Job[] = []

export function novelTranslationConcurrencyForBackend(backend: string | undefined | null): number {
  switch (backend) {
    case 'deepseek':
    case 'anthropic':
      return 5
    case 'ollama':
    default:
      return 1
  }
}

/** Configure reader-side concurrency from the server's novel translation backend. */
export function setNovelTranslationMaxConcurrent(n: number) {
  maxConcurrent = Math.max(1, Math.floor(n) || 1)
  pumpQueue()
}

export function setNovelTranslationBackend(backend: string | undefined | null) {
  setNovelTranslationMaxConcurrent(novelTranslationConcurrencyForBackend(backend))
}

function cacheKey(cacheId: string, page: number) {
  return `${cacheId}:${page}`
}

function notifyJob(job: Job, status: NovelTranslateQueueStatus) {
  for (const listener of job.listeners) {
    try {
      listener(status)
    } catch {
      /* ignore subscriber errors */
    }
  }
}

function notifyQueuedPositions() {
  for (let i = 0; i < waitQueue.length; i++) {
    notifyJob(waitQueue[i], { phase: 'queued', position: i + 1 })
  }
}

function removeFromWaitQueue(job: Job) {
  const idx = waitQueue.indexOf(job)
  if (idx >= 0) {
    waitQueue.splice(idx, 1)
    notifyQueuedPositions()
  }
}

function releaseSlot() {
  activeCount = Math.max(0, activeCount - 1)
  pumpQueue()
}

function pumpQueue() {
  while (activeCount < maxConcurrent && waitQueue.length > 0) {
    const next = waitQueue.shift()!
    if (next.controller.signal.aborted) {
      next.grantSlot = undefined
      continue
    }
    activeCount++
    next.acquired = true
    notifyJob(next, { phase: 'active', position: 0 })
    const grant = next.grantSlot
    next.grantSlot = undefined
    grant?.()
  }
  notifyQueuedPositions()
}

function waitForSlot(job: Job): Promise<void> {
  if (job.controller.signal.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'))
  }

  // Fairness: only skip the queue when nobody else is waiting.
  if (activeCount < maxConcurrent && waitQueue.length === 0) {
    activeCount++
    job.acquired = true
    notifyJob(job, { phase: 'active', position: 0 })
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    job.grantSlot = () => resolve()
    const onAbort = () => {
      removeFromWaitQueue(job)
      job.grantSlot = undefined
      reject(new DOMException('Aborted', 'AbortError'))
    }
    job.controller.signal.addEventListener('abort', onAbort, { once: true })
    waitQueue.push(job)
    notifyJob(job, { phase: 'queued', position: waitQueue.length })
    notifyQueuedPositions()
    pumpQueue()
  })
}

function finishJob(job: Job) {
  if (jobs.get(job.key) === job) {
    jobs.delete(job.key)
  }
  removeFromWaitQueue(job)
  if (job.acquired) {
    job.acquired = false
    releaseSlot()
  }
}

export function getCachedNovelTranslation(
  cacheId: string,
  page: number,
): string | undefined {
  return textCache.get(cacheKey(cacheId, page))
}

/** Current queue status for a page job, if any. */
export function getNovelTranslationJobStatus(
  cacheId: string,
  page: number,
): NovelTranslateQueueStatus | null {
  const job = jobs.get(cacheKey(cacheId, page))
  if (!job || job.controller.signal.aborted) return null
  if (job.acquired) return { phase: 'active', position: 0 }
  const idx = waitQueue.indexOf(job)
  if (idx >= 0) return { phase: 'queued', position: idx + 1 }
  return null
}

export function clearNovelTranslationCache() {
  const pending = [...jobs.values()]
  jobs.clear()
  waitQueue.length = 0
  activeCount = 0
  textCache.clear()
  for (const job of pending) {
    job.grantSlot = undefined
    job.acquired = false
    job.controller.abort()
  }
}

export function invalidateNovelTranslationPage(cacheId: string, page: number) {
  textCache.delete(cacheKey(cacheId, page))
  const job = jobs.get(cacheKey(cacheId, page))
  if (!job) return
  jobs.delete(job.key)
  removeFromWaitQueue(job)
  const wasAcquired = job.acquired
  job.acquired = false
  job.grantSlot = undefined
  job.controller.abort()
  if (wasAcquired) releaseSlot()
}

export async function fetchNovelTranslation(
  server: any,
  cacheId: string,
  page: number,
  force: boolean,
  apiOpts?: {
    bookId?: string
    seriesId: string
    type: string
    num: string
    subNum: string
  },
  onStatus?: StatusListener,
): Promise<string | null> {
  const key = cacheKey(cacheId, page)

  if (!force) {
    const cached = textCache.get(key)
    if (cached != null) return cached
  } else {
    invalidateNovelTranslationPage(cacheId, page)
  }

  const existing = jobs.get(key)
  if (existing) {
    if (onStatus) {
      existing.listeners.add(onStatus)
      const status = getNovelTranslationJobStatus(cacheId, page)
      if (status) onStatus(status)
    }
    try {
      return await existing.promise
    } catch {
      return null
    } finally {
      if (onStatus) existing.listeners.delete(onStatus)
    }
  }

  const controller = new AbortController()
  const job: Job = {
    key,
    cacheId,
    page,
    force,
    controller,
    promise: null as unknown as Promise<string | null>,
    listeners: new Set(),
    acquired: false,
  }
  if (onStatus) job.listeners.add(onStatus)

  job.promise = (async () => {
    try {
      await waitForSlot(job)
      if (controller.signal.aborted) return null

      const bookId = apiOpts?.bookId ?? ''
      const text = await readTranslatedNovelPage(
        server,
        bookId,
        page,
        force,
        controller.signal,
        bookId
          ? undefined
          : apiOpts
            ? {
                seriesId: apiOpts.seriesId,
                type: apiOpts.type,
                num: apiOpts.num,
                subNum: apiOpts.subNum,
              }
            : undefined,
      )
      if (text != null) {
        textCache.set(key, text)
      }
      return text
    } finally {
      if (onStatus) job.listeners.delete(onStatus)
      finishJob(job)
    }
  })()

  jobs.set(key, job)
  return job.promise
}
